/**
 * The browser's Supabase client.
 *
 * It holds the **anon key and nothing else**. That key is safe in a bundle
 * because Row Level Security decides what it can reach, and 0003–0010 are what
 * make that true.
 *
 * The service role key is a different matter: it carries BYPASSRLS, so it is
 * not "a more powerful key", it is a key with no tenant boundary at all. It
 * lives in server environment configuration, is never prefixed `VITE_`, and is
 * not importable from this directory. `pnpm check:tenant` fails the build if the
 * string appears anywhere under `apps/`.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { withIssuedAtGrace } from '@timely/domain';
import { publicConfig } from '../config/env';
import { fetchWithTimeout } from './fetchWithTimeout';

let client: SupabaseClient | null = null;

export function supabase(): SupabaseClient {
    if (client) return client;
    const config = publicConfig();
    client = createClient(config.supabaseUrl, config.supabaseAnonKey, {
        auth: {
            persistSession: true,
            autoRefreshToken: true,
            // The verification and recovery links arrive with their tokens in
            // the URL fragment; the client picks them up and then the callback
            // screen cleans the address bar.
            detectSessionInUrl: true,
            flowType: 'pkce',
        },
        // A request that never answers ends, and the screen shows its own
        // "Try again" instead of a skeleton for ever (Session 17). A Data API
        // request refused as "issued at future" — the first after signing in
        // or a refresh, on a platform clock fault — is made once more after
        // the token's second has passed (D27; `withIssuedAtGrace`).
        global: { fetch: withIssuedAtGrace(fetchWithTimeout()) },
    });
    return client;
}

/**
 * Only for tests: hand in a double and get the real one back afterwards.
 *
 * A seam rather than a module mock, so the screens are exercised through the
 * same call sites they use in production.
 */
export function __setSupabaseForTests(fake: SupabaseClient | null): void {
    client = fake;
}
